import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { LOGIN_CODE_EXPIRY_MINUTES } from '@/config/constants'
import { AppLogo } from '@/components/ui/AppLogo'

export const VerifyCodePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { validateCode } = useAuth()
  const email = (location.state as { email?: string })?.email || ''

  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [timeLeft, setTimeLeft] = useState(LOGIN_CODE_EXPIRY_MINUTES * 60)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true })
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [email, navigate])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setError('')

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleSubmit(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      inputRefs.current[5]?.focus()
      handleSubmit(pastedData)
    }
  }

  const handleSubmit = async (codeValue?: string) => {
    const codeToSubmit = codeValue || code.join('')
    if (codeToSubmit.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres complet')
      return
    }

    setIsLoading(true)
    setError('')
    try {
      await validateCode({ email, code: parseInt(codeToSubmit, 10) })
      navigate('/', { replace: true })
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Code invalide. Veuillez réessayer.')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = () => {
    navigate('/login', { replace: true })
  }

  if (!email) {
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 sm:mb-12">
          <AppLogo size="lg" className="mx-auto mb-6 shadow-lg" />
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
            Vérifiez votre email
          </h1>
          <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
            Nous avons envoyé un code à 6 chiffres à
            <br />
            <span className="font-medium text-[var(--color-text-primary)]">{email}</span>
          </p>
        </div>

        <Card padding="lg">
          <form
            onSubmit={e => {
              e.preventDefault()
              handleSubmit()
            }}
            className="space-y-8"
          >
            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                role="alert"
              >
                {error}
              </div>
            )}

            <div>
              <label className="block text-base sm:text-lg font-medium text-[var(--color-text-primary)] mb-4 sm:mb-5">
                Entrez le code de vérification
              </label>
              <div className="flex gap-2 sm:gap-3 w-full" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => {
                      inputRefs.current[index] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleCodeChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    className="flex-1 aspect-square w-0 text-center text-2xl sm:text-3xl font-semibold border-2 border-[var(--color-border)] rounded-xl focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:outline-none transition-all"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            {timeLeft > 0 ? (
              <p className="text-center text-sm sm:text-base text-[var(--color-text-muted)]">
                Le code expire dans{' '}
                <span className="font-semibold text-[var(--color-text-primary)]">
                  {formatTime(timeLeft)}
                </span>
              </p>
            ) : (
              <div className="text-center space-y-4">
                <p className="text-sm sm:text-base text-[var(--color-text-muted)]">
                  Le code a expiré
                </p>
                <Button type="button" variant="outline" onClick={handleResend} fullWidth size="lg">
                  Demander un nouveau code
                </Button>
              </div>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                fullWidth
                isLoading={isLoading}
                disabled={code.some(digit => !digit) || timeLeft === 0}
                size="lg"
              >
                Vérifier le code
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm sm:text-base text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-medium transition-colors"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  )
}
